// Robustní import utility pro CRM
import * as XLSX from 'xlsx';
import he from 'he';

// Parsování boolean hodnot
export const parseBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (!value) return false;
  
  const str = String(value).toLowerCase().trim();
  return str === 'true' || str === '1' || str === 'ano' || str === 'yes' || str === 'y' || str === 'a';
};

// Čištění textu od HTML entit a normalizace
export const cleanText = (text) => {
  if (!text) return '';
  
  // Převod na string
  let cleaned = String(text);
  
  // Dekódování HTML entit (&lt;, &gt;, &amp;, etc.)
  cleaned = he.decode(cleaned);
  
  // Odstranění HTML tagů (<br />, <div>, etc.)
  cleaned = cleaned.replace(/<[^>]*>/g, ' ');
  
  // Normalizace bílých znaků (více mezer → jedna mezera)
  cleaned = cleaned.replace(/\s+/g, ' ');
  
  // Odstranění leading/trailing whitespace
  cleaned = cleaned.trim();
  
  return cleaned;
};

// Rozdělení více hodnot v jednom poli
export const splitMultipleValues = (text, separators = [';', ',', '\n']) => {
  if (!text) return [];
  
  let values = [String(text)];
  
  // Postupně rozděluj podle každého separátoru
  separators.forEach(separator => {
    values = values.flatMap(value => 
      value.split(separator).map(v => v.trim()).filter(Boolean)
    );
  });
  
  return values.filter(Boolean);
};

// Čištění telefonních čísel
export const cleanPhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Převod na string a základní čištění
  let cleaned = String(phone).trim();
  
  // Pokud je to číslo z Excelu (např. vědecká notace), převeď zpět na normální číslo
  if (!isNaN(phone) && !cleaned.includes('+')) {
    // Je to číslo - převeď na string bez vědecké notace
    cleaned = Number(phone).toString();
  }
  
  // Speciální ošetření pro vědeckou notaci (např. 4.2077777E+11)
  if (cleaned.includes('E+') || cleaned.includes('e+')) {
    try {
      const number = parseFloat(cleaned);
      if (!isNaN(number) && number > 0) {
        cleaned = Math.round(number).toString();
      }
    } catch (e) {
      // Pokud se nepodaří převést, pokračuj s původním řetězcem
    }
  }
  
  // Odstranění všech nealfanumerických znaků kromě + a zachování pouze čísel a +
  // Ale nejdříve zkontroluj, jestli není více + znaků (problém "+420725+11")
  const plusCount = (cleaned.match(/\+/g) || []).length;
  if (plusCount > 1) {
    // Pokud je více + znaků, ponechej pouze první část před druhým +
    const firstPlusIndex = cleaned.indexOf('+');
    const secondPlusIndex = cleaned.indexOf('+', firstPlusIndex + 1);
    if (secondPlusIndex > 0) {
      cleaned = cleaned.substring(0, secondPlusIndex);
    }
  }
  
  cleaned = cleaned.replace(/[^\d+]/g, '');
  
  // Pokud je číslo příliš krátké, vrať prázdný string
  if (cleaned.length < 9) return '';
  
  // Ošetření různých formátů českých čísel
  
  // Pokud začíná na 00420, nahraď za +420
  if (cleaned.startsWith('00420')) {
    cleaned = cleaned.replace('00420', '+420');
  }
  // Pokud začíná na 420 bez +, přidej +
  else if (cleaned.startsWith('420') && !cleaned.startsWith('+420')) {
    cleaned = '+' + cleaned;
  }
  // Pokud začíná českým číslem bez předvolby (7, 6, 9)
  else if (/^[679]\d{8}$/.test(cleaned)) {
    cleaned = '+420' + cleaned;
  }
  // Pokud už má +420, nech to tak
  else if (cleaned.startsWith('+420')) {
    // Už je v pořádku
  }
  // Pokud má jiné mezinárodní číslo s +, nech to tak
  else if (cleaned.startsWith('+')) {
    // Jiné mezinárodní číslo
  }
  // Pokud je to číslo bez jakékoliv předvolby a má správnou délku pro české číslo
  else if (/^\d{9}$/.test(cleaned)) {
    cleaned = '+420' + cleaned;
  }
  
  // Validace délky pro české čísla
  if (cleaned.startsWith('+420')) {
    // České číslo by mělo mít +420 + 9 číslic = 13 znaků
    if (cleaned.length !== 13) {
      // Pokud má špatnou délku, zkus to opravit
      const digits = cleaned.replace('+420', '');
      if (digits.length === 9) {
        cleaned = '+420' + digits;
      } else if (digits.length > 9) {
        // Pokud má více číslic, vezmi pouze prvních 9
        cleaned = '+420' + digits.substring(0, 9);
      } else {
        return ''; // Nevalidní číslo - příliš málo číslic
      }
    }
  }
  
  // Konečná validace - musí mít alespoň 10 znaků
  if (cleaned.length < 10) return '';
  
  return cleaned;
};

// Čištění emailové adresy
export const cleanEmail = (email) => {
  if (!email) return '';
  
  let cleaned = String(email).toLowerCase().trim();
  
  // Základní validace emailu
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(cleaned) ? cleaned : '';
};

// Normalizace datumu
export const normalizeDate = (dateString) => {
  if (!dateString) return null;
  
  const str = String(dateString).trim();
  
  // Pokus o parsování různých formátů
  const formats = [
    // DD.MM.YYYY HH:mm:ss
    /^(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})$/,
    // DD.MM.YYYY HH:mm
    /^(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{1,2}):(\d{2})$/,
    // DD.MM.YYYY
    /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/,
    // YYYY-MM-DD HH:mm:ss
    /^(\d{4})-(\d{1,2})-(\d{1,2})\s+(\d{1,2}):(\d{2}):(\d{2})$/,
    // YYYY-MM-DD HH:mm
    /^(\d{4})-(\d{1,2})-(\d{1,2})\s+(\d{1,2}):(\d{2})$/,
    // YYYY-MM-DD
    /^(\d{4})-(\d{1,2})-(\d{1,2})$/,
  ];
  
  // Pokus o český formát DD.MM.YYYY
  const czechMatch = str.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
  if (czechMatch) {
    const [_, day, month, year, hour = '0', minute = '0', second = '0'] = czechMatch;
    return new Date(
      parseInt(year),
      parseInt(month) - 1, // měsíce jsou 0-indexované
      parseInt(day),
      parseInt(hour),
      parseInt(minute),
      parseInt(second)
    );
  }
  
  // Pokus o ISO formát
  const isoMatch = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
  if (isoMatch) {
    const [_, year, month, day, hour = '0', minute = '0', second = '0'] = isoMatch;
    return new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hour),
      parseInt(minute),
      parseInt(second)
    );
  }
  
  // Pokus o standardní parsování
  const parsed = new Date(str);
  return isNaN(parsed.getTime()) ? null : parsed;
};

// Detekce typu souboru
export const detectFileType = (file) => {
  const extension = file.name.toLowerCase().split('.').pop();
  const mimeType = file.type.toLowerCase();
  
  if (extension === 'csv' || mimeType.includes('csv')) {
    return 'csv';
  }
  
  if (['xlsx', 'xls'].includes(extension) || 
      mimeType.includes('spreadsheet') || 
      mimeType.includes('excel')) {
    return 'excel';
  }
  
  return 'unknown';
};

// Čtení Excel souboru
export const readExcelFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const arrayBuffer = new Uint8Array(e.target.result);
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        
        // Použij první sheet
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        
        // Převod na JSON s hlavičkami
        const jsonData = XLSX.utils.sheet_to_json(sheet, { 
          header: 1,
          raw: false, // převede všechno na stringy
          dateNF: 'dd.mm.yyyy' // formát datumu
        });
        
        if (jsonData.length === 0) {
          reject(new Error('Soubor je prázdný'));
          return;
        }
        
        // První řádek jsou hlavičky
        const headers = jsonData[0];
        const rows = jsonData.slice(1);
        
        // Převod na objekty
        const parsedData = rows.map(row => {
          const obj = {};
          headers.forEach((header, index) => {
            obj[header] = row[index] || '';
          });
          return obj;
        });
        
        resolve(parsedData);
      } catch (error) {
        reject(new Error(`Chyba při čtení Excel souboru: ${error.message}`));
      }
    };
    
    reader.onerror = () => reject(new Error('Chyba při čtení souboru'));
    reader.readAsArrayBuffer(file);
  });
};

// Čtení CSV souboru s robustním parsováním
export const readCSVFile = (file, delimiter = ';') => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        let content = e.target.result;
        
        // Detekce kódování a konverze
        if (!content.includes('ř') && !content.includes('š') && !content.includes('č')) {
          // Možná je to Windows-1250 nebo jiné kódování
          console.warn('Možná chybné kódování souboru');
        }
        
        // Rozdělení na řádky
        const lines = content.split(/\r?\n/).filter(line => line.trim());
        
        if (lines.length === 0) {
          reject(new Error('Soubor je prázdný'));
          return;
        }
        
        // První řádek jsou hlavičky
        const headers = lines[0].split(delimiter).map(h => h.trim().replace(/['"]/g, ''));
        const rows = lines.slice(1);
        
        // Parsování řádků
        const parsedData = rows.map((line, index) => {
          const values = [];
          let current = '';
          let inQuotes = false;
          
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === delimiter && !inQuotes) {
              values.push(current);
              current = '';
            } else {
              current += char;
            }
          }
          values.push(current); // poslední hodnota
          
          // Vytvoření objektu
          const obj = {};
          headers.forEach((header, i) => {
            obj[header] = values[i] ? values[i].trim().replace(/^["']|["']$/g, '') : '';
          });
          
          return obj;
        });
        
        resolve(parsedData);
      } catch (error) {
        reject(new Error(`Chyba při čtení CSV souboru: ${error.message}`));
      }
    };
    
    reader.onerror = () => reject(new Error('Chyba při čtení souboru'));
    reader.readAsText(file, 'utf-8');
  });
};

// Hlavní funkce pro čtení souboru (automatická detekce typu)
export const readImportFile = async (file) => {
  const fileType = detectFileType(file);
  
  switch (fileType) {
    case 'csv':
      return await readCSVFile(file);
    case 'excel':
      return await readExcelFile(file);
    default:
      throw new Error(`Nepodporovaný typ souboru: ${file.name}. Podporované typy: CSV, Excel (xlsx, xls)`);
  }
};

// Transformace dat do formátu pro CRM
export const transformImportData = (rawData, columnMapping = {}) => {
  return rawData.map(row => {
    
    // Pokud je mapování prázdné, použij původní logiku
    if (!columnMapping || Object.keys(columnMapping).length === 0) {
      // Fallback na původní automatické mapování
      const normalizedRow = {};
      Object.keys(row).forEach(key => {
        const lowerKey = key.toLowerCase();
        
        // Automatické mapování běžných názvů - přesnější
        if (lowerKey === 'ico' || lowerKey === 'ičo') {
          normalizedRow.ico = row[key];
        } else if (lowerKey === 'name' || lowerKey === 'název' || lowerKey === 'nazev') {
          normalizedRow.name = row[key];
        } else if (lowerKey === 'address' || lowerKey === 'adresa') {
          normalizedRow.address = row[key];
        } else if (lowerKey === 'operator' || lowerKey === 'operátor') {
          normalizedRow.operator = row[key];
        } else if (lowerKey === 'status' || lowerKey === 'stav') {
          normalizedRow.status = row[key];
        } else if (lowerKey === 'contactname' || lowerKey === 'kontakt' || lowerKey.includes('jméno kontaktu')) {
          normalizedRow.contactName = row[key];
        } else if (lowerKey === 'contactphone' || lowerKey.includes('telefon')) {
          normalizedRow.contactPhone = row[key];
        } else if (lowerKey === 'contactemail' || lowerKey.includes('email')) {
          normalizedRow.contactEmail = row[key];
        } else if (lowerKey === 'activitytext' || lowerKey.includes('aktivita')) {
          normalizedRow.activityText = row[key];
        } else if (lowerKey === 'nextactiondate' || lowerKey.includes('nextaction') || lowerKey.includes('dalsi') || lowerKey.includes('další')) {
          normalizedRow.nextActionDate = row[key];
        } else if (lowerKey === 'forecast') {
          normalizedRow.forecast = row[key];
        } else if (lowerKey === 'bmsl') {
          normalizedRow.bmsl = row[key];
        } else if (lowerKey === 'rsenddate' || lowerKey.includes('konec rs') || lowerKey.includes('rs konec')) {
          normalizedRow.rsEndDate = row[key];
        } else if (lowerKey === 'donotconnect' || lowerKey === 'nepropojovat' || lowerKey === 'nespamovat') {
          normalizedRow.doNotConnect = row[key];
        } else if (lowerKey === 'kraj' || lowerKey === 'region') {
          normalizedRow.kraj = row[key];
        } else if (/^kontakt[_ ]?1[_ -]+jméno$|^kontakt[_ ]?1[_ -]+jmeno$|^contact[_ ]?1[_ -]+name$|^contact1name$/i.test(lowerKey)) {
          normalizedRow.contact1Name = row[key];
        } else if (/^kontakt[_ ]?1[_ -]+telefon$|^contact[_ ]?1[_ -]+phone$|^contact1phone$/i.test(lowerKey)) {
          normalizedRow.contact1Phone = row[key];
        } else if (/^kontakt[_ ]?1[_ -]+email$|^contact[_ ]?1[_ -]+email$|^contact1email$/i.test(lowerKey)) {
          normalizedRow.contact1Email = row[key];
        } else if (/^kontakt[_ ]?2[_ -]+jméno$|^kontakt[_ ]?2[_ -]+jmeno$|^contact[_ ]?2[_ -]+name$|^contact2name$/i.test(lowerKey)) {
          normalizedRow.contact2Name = row[key];
        } else if (/^kontakt[_ ]?2[_ -]+telefon$|^contact[_ ]?2[_ -]+phone$|^contact2phone$/i.test(lowerKey)) {
          normalizedRow.contact2Phone = row[key];
        } else if (/^kontakt[_ ]?2[_ -]+email$|^contact[_ ]?2[_ -]+email$|^contact2email$/i.test(lowerKey)) {
          normalizedRow.contact2Email = row[key];
        } else if (/^kontakt[_ ]?3[_ -]+jméno$|^kontakt[_ ]?3[_ -]+jmeno$|^contact[_ ]?3[_ -]+name$|^contact3name$/i.test(lowerKey)) {
          normalizedRow.contact3Name = row[key];
        } else if (/^kontakt[_ ]?3[_ -]+telefon$|^contact[_ ]?3[_ -]+phone$|^contact3phone$/i.test(lowerKey)) {
          normalizedRow.contact3Phone = row[key];
        } else if (/^kontakt[_ ]?3[_ -]+email$|^contact[_ ]?3[_ -]+email$|^contact3email$/i.test(lowerKey)) {
          normalizedRow.contact3Email = row[key];
        } else if (/^kontakt[_ ]?4[_ -]+jméno$|^kontakt[_ ]?4[_ -]+jmeno$|^contact[_ ]?4[_ -]+name$|^contact4name$/i.test(lowerKey)) {
          normalizedRow.contact4Name = row[key];
        } else if (/^kontakt[_ ]?4[_ -]+telefon$|^contact[_ ]?4[_ -]+phone$|^contact4phone$/i.test(lowerKey)) {
          normalizedRow.contact4Phone = row[key];
        } else if (/^kontakt[_ ]?4[_ -]+email$|^contact[_ ]?4[_ -]+email$|^contact4email$/i.test(lowerKey)) {
          normalizedRow.contact4Email = row[key];
        } else {
          // Zachovej původní klíč, pokud neodpovídá ničemu
          normalizedRow[key] = row[key];
        }
      });
      
      row = normalizedRow;
    }
    
    // Čištění a transformace hodnot
    const cleaned = {
      ico: cleanText(row.ico),
      name: cleanText(row.name),
      address: cleanText(row.address),
      kraj: cleanText(row.kraj),
      operator: cleanText(row.operator),
      status: cleanText(row.status),
      forecast: cleanText(row.forecast),
      bmsl: cleanText(row.bmsl),
      rsEndDate: normalizeDate(row.rsEndDate),
      doNotConnect: parseBoolean(row.doNotConnect),
      nextActionDate: normalizeDate(row.nextActionDate),
    };
    
    // Zpracování kontaktů - sloučení ze starého jednoho pole + 4 číslovaných polí
    const contactNames = splitMultipleValues(row.contactName);
    const contactPhones = splitMultipleValues(row.contactPhone).map(cleanPhoneNumber);
    const contactEmails = splitMultipleValues(row.contactEmail).map(cleanEmail);
    
    // Vytvoření pole kontaktů ze starého způsobu (jedno pole)
    const maxContacts = Math.max(contactNames.length, contactPhones.length, contactEmails.length);
    const contacts = [];
    for (let i = 0; i < maxContacts; i++) {
      const contact = {
        name: contactNames[i] || '',
        phone: contactPhones[i] || '',
        email: contactEmails[i] || ''
      };
      if (contact.name || contact.phone || contact.email) {
        contacts.push(contact);
      }
    }
    
    // Přidání kontaktů z číslovaných polí (Kontakt 1-4)
    for (let n = 1; n <= 4; n++) {
      const cName  = cleanText(row[`contact${n}Name`]);
      const cPhone = cleanPhoneNumber(cleanText(row[`contact${n}Phone`] || ''));
      const cEmail = cleanEmail(cleanText(row[`contact${n}Email`] || ''));
      if (cName || cPhone || cEmail) {
        // Přidej pouze pokud ještě takový kontakt není (dedup podle emailu/telefonu)
        const isDup = contacts.some(c =>
          (cEmail && c.email === cEmail) ||
          (cPhone && c.phone === cPhone)
        );
        if (!isDup) {
          contacts.push({ name: cName || '', phone: cPhone || '', email: cEmail || '' });
        }
      }
    }
    
    // Zpracování aktivit
    const activities = [];
    if (row.activityText) {
      // Nejdříve rozděl podle newline (prioritní separátor), pak podle středníku
      let activityTexts = splitMultipleValues(row.activityText, ['\n']);
      // Pokud není rozděleno newline, zkus středník (ale opatrně kvůli HTML entitám)
      if (activityTexts.length === 1) {
        activityTexts = splitMultipleValues(row.activityText, [';']);
      }
      
      activityTexts.forEach(text => {
        const cleanedText = cleanText(text);
        if (cleanedText) {
          activities.push({
            text: cleanedText,
            createdAt: new Date(),
            nextActionDate: cleaned.nextActionDate || undefined
          });
        }
      });
    }
    
    return {
      ...cleaned,
      contacts,
      activities: activities.reverse(), // Obrátíme pořadí aktivit, aby se první stala poslední
      _originalData: row._originalData || row, // Zachovej původní data z mapování nebo celý řádek
      _ozValue: row._ozValue // Zachovej OZ hodnotu
    };
  });
};

// Validace importovaných dat
export const validateImportData = (data) => {
  const errors = [];
  const warnings = [];
  
  data.forEach((row, index) => {
    const rowNum = index + 1;
    
    // Povinná pole
    if (!row.name) {
      errors.push(`Řádek ${rowNum}: Chybí název firmy`);
    }
    
    // Validace IČO
    if (row.ico && !/^\d{8}$/.test(row.ico)) {
      warnings.push(`Řádek ${rowNum}: IČO '${row.ico}' má neplatný formát (očekáváno 8 číslic)`);
    }
    
    // Validace emailů
    row.contacts?.forEach((contact, cIndex) => {
      if (contact.email && !contact.email.includes('@')) {
        warnings.push(`Řádek ${rowNum}, kontakt ${cIndex + 1}: Email '${contact.email}' má neplatný formát`);
      }
    });
    
    // Validace telefonů
    row.contacts?.forEach((contact, cIndex) => {
      if (contact.phone && contact.phone.length < 9) {
        warnings.push(`Řádek ${rowNum}, kontakt ${cIndex + 1}: Telefon '${contact.phone}' je příliš krátký`);
      }
    });
  });
  
  return { errors, warnings };
};
